import { useState } from 'react'
import { GraduationCap, MapPin, Video } from 'lucide-react'
import { Body, Caption } from '@meiuca/ui-components'
import { formatDistance } from '../types/arena'
import { getModalityLabel, type TrainingCenterListItem } from '../types/trainingCenter'

interface TrainingCenterCardProps {
  center: TrainingCenterListItem
  onClick?: () => void
  className?: string
}

export function TrainingCenterCard({ center, onClick, className }: TrainingCenterCardProps) {
  const [coverFailed, setCoverFailed] = useState(false)
  const [logoFailed, setLogoFailed] = useState(false)
  const showCover = !!center.coverImageURL && !coverFailed

  // Endereço fica de fora: um centro pode treinar em vários lugares, e mostrar
  // só um passa a informação errada. Com mais de um, o card diz quantos são e
  // o perfil lista todos.
  const locationLabel =
    center.locationsCount > 1
      ? `${center.locationsCount} locais de treino`
      : center.locationName ?? (center.offersRemoteConsulting ? 'Atendimento online' : null)

  return (
    <div
      onClick={onClick}
      className={`rounded-lg overflow-hidden bg-background-white border border-border-light hover:shadow-lg transition-shadow cursor-pointer ${className ?? ''}`}
    >
      <div className="relative w-full overflow-hidden" style={{ paddingTop: '50%', height: 0 }}>
        {showCover ? (
          <img
            src={center.coverImageURL ?? ''}
            alt={center.name}
            onError={() => setCoverFailed(true)}
            className="absolute top-0 left-0 w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-brand-primary/30 to-brand-primary/10 flex items-center justify-center">
            <GraduationCap className="w-10 h-10 text-brand-primary/40" />
          </div>
        )}

        {center.logoURL && !logoFailed && (
          <div className="absolute bottom-2 left-2 w-12 h-12 rounded-lg overflow-hidden bg-white border-2 border-white shadow-md">
            <img
              src={center.logoURL}
              alt={`Logo de ${center.name}`}
              onError={() => setLogoFailed(true)}
              className="w-full h-full bg-white object-contain p-1"
            />
          </div>
        )}

        {center.offersRemoteConsulting && (
          <div className="absolute top-2 right-2 bg-background-white/90 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1">
            <Video className="w-3 h-3 text-brand-primary" />
            <Caption className="font-semibold text-text-primary">Atende online</Caption>
          </div>
        )}
      </div>

      <div className="p-4 space-y-2">
        <Body className="font-semibold text-text-primary line-clamp-1">{center.name}</Body>

        {center.modalities.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {center.modalities.slice(0, 3).map((modality) => (
              <span
                key={modality}
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-brand-primary/10 text-brand-primary"
              >
                {getModalityLabel(modality)}
              </span>
            ))}
            {center.modalities.length > 3 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-text-secondary">
                +{center.modalities.length - 3}
              </span>
            )}
          </div>
        )}

        {locationLabel && (
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-text-tertiary flex-shrink-0 mt-0.5" />
            <Caption className="text-text-secondary line-clamp-1">{locationLabel}</Caption>
          </div>
        )}

        {typeof center.distanceMeters === 'number' && (
          <Caption className="text-text-tertiary">{formatDistance(center.distanceMeters)}</Caption>
        )}
      </div>
    </div>
  )
}
